import React from 'react';
import { Id } from '../../types/Id';
import {
  StackedRibbonListMinimalProps,
  StackedRibbonListMinimalVirtualizedProps,
} from '../../types/StackedRibbonListComponentTypes';
import { RibbonListVirtualizedHandles } from '../List/RibbonListHandles';
import { RibbonListVirtualized } from '../List/RibbonListVirtualized';
import { RibbonListWrapper } from '../List/RibbonListWrapper';
import { useRibbonListWrapper } from '../List/useRibbonListWrapper';

export interface Props<TId extends Id, TItem>
  extends StackedRibbonListMinimalProps<TId, TItem>,
    StackedRibbonListMinimalVirtualizedProps<TId, TItem> {}

/**
 * A StackedRibbonList without a header section whose contents are virtualized in the DOM.
 */
export const StackedRibbonListMinimalVirtualized = React.memo(
  React.forwardRef(
    <TId extends Id, TItem>(
      props: Props<TId, TItem>,
      ref: React.Ref<RibbonListVirtualizedHandles>,
    ) => {
      const { ribbonListProps, ribbonListWrapperProps } = useRibbonListWrapper(props);

      const {
        estimatedItemSize,
        isItemLoaded,
        loadMoreItems,
        minimumBatchSize,
        unstable_infiniteScrollItemCount: infiniteScrollItemCount,
      } = props;

      return (
        <RibbonListWrapper {...ribbonListWrapperProps}>
          <RibbonListVirtualized
            ref={ref}
            {...ribbonListProps}
            estimatedItemSize={estimatedItemSize}
            isItemLoaded={isItemLoaded}
            loadMoreItems={loadMoreItems}
            minimumBatchSize={minimumBatchSize}
            unstable_infiniteScrollItemCount={infiniteScrollItemCount}
          />
        </RibbonListWrapper>
      );
    },
  ),
) as <TId extends Id, TItem>(
  props: Props<TId, TItem> & {
    ref?: React.Ref<RibbonListVirtualizedHandles>;
  },
) => JSX.Element;
